import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Divider,
  Menu,
  MenuItem,
  CircularProgress,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import RoomRoundedIcon from "@mui/icons-material/RoomRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PublicIcon from "@mui/icons-material/Public";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import ChatIcon from "@mui/icons-material/Chat";
import CancelIcon from "@mui/icons-material/Cancel";
import OrderDetailsDialog from "./OrderDetailsDialog";
import { useRequests } from "../../hooks/useRequests"; // <- usado para persistir o status

/* ===== helpers ===== */
const channelIconByType = {
  WhatsApp: <WhatsAppIcon fontSize="small" />,
  App: <PhoneIphoneIcon fontSize="small" />,
  Web: <PublicIcon fontSize="small" />,
  Messenger: <ChatIcon fontSize="small" />,
};

// default: Web
const getChannelIcon = (type) => channelIconByType[type] ?? <PublicIcon fontSize="small" />;

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const money = (v) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : v ?? "—";

// -------- Status helpers --------
function normalizeStatusInput(v) {
  if (v === null || v === undefined) return 0;
  if (v === "" || v === "0" || v === 0) return 0;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 5 ? n : 0;
}

const STATUS_OPTIONS = [
  { value: 0, label: "Pendente",        colorKey: "pending" },
  { value: 1, label: "Em Atendimento",  colorKey: "inProgress" },
  { value: 2, label: "Concluída",       colorKey: "success" },
  { value: 3, label: "Contato Futuro",  colorKey: "future" },
  { value: 4, label: "Perdida",         colorKey: "unsuccessful" },
  { value: 5, label: "Cancelada",       colorKey: "canceled" },
];

const statusDetailsOf = (raw) => {
  const n = normalizeStatusInput(raw);
  return STATUS_OPTIONS.find((s) => s.value === n) || STATUS_OPTIONS[0];
};

const StatusDot = ({ status, loading = false }) => {
  const theme = useTheme();
  const det = statusDetailsOf(status);
  const bg =
    (theme.palette.status && theme.palette.status[det.colorKey]) ||
    (det.value === 2
      ? theme.palette.success.main
      : det.value === 5
      ? theme.palette.error.main
      : theme.palette.grey[500]);

  return (
    <Tooltip title={det.label}>
      <Box sx={{ position: "relative", width: 14, height: 14 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: bg,
            border: "1px solid",
            borderColor: "common.white",
            boxShadow: 1,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
        {loading && (
          <CircularProgress
            size={14}
            thickness={8}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const InfoPill = ({ label, value, highlight }) => (
  <Box
    sx={{
      border: (t) => `1px solid ${t.palette.divider}`,
      bgcolor: highlight ? "primary.main" : "action.hover",
      color: highlight ? "primary.contrastText" : "text.primary",
      borderRadius: 1.5,
      px: 1,
      py: 0.75,
    }}
  >
    <Typography variant="caption" sx={{ opacity: 0.85, display: "block", lineHeight: 1 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
      {value}
    </Typography>
  </Box>
);

const InfoRow = ({ icon, children, after }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "18px 1fr auto",
      columnGap: 1,
      alignItems: "flex-start",
      rowGap: 0.5,
    }}
  >
    <Box sx={{ mt: "2px", color: "text.secondary" }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
    {after || null}
  </Box>
);

const textWrap = {
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  hyphens: "auto",
};

/** Barra superior: data (esq) + status + ações (dir) — topo do card */
const TopMetaBar = ({ orderCode, createdAtLabel, status, statusLoading, onOpenDetails, onOpenChat, onOpenMenu }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 0.5,
      mt: 0.25,
    }}
  >
    {/* Esquerda: código + horário */}
    <Stack spacing={0.1} sx={{ color: "text.secondary" }}>
      {!!orderCode && (
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ lineHeight: 1 }}
          title={`#${orderCode}`}
        >
          #{orderCode}
        </Typography>
      )}
      <Stack direction="row" spacing={0.25} alignItems="center">
        <AccessTimeIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" sx={{ lineHeight: 1 }}>
          {createdAtLabel || ""}
        </Typography>
      </Stack>
    </Stack>

    {/* Direita: status + ações */}
    <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
      <StatusDot status={status} loading={statusLoading} />

      <Tooltip title="Ver pedido">
        <IconButton
          size="small"
          aria-label="ver pedido"
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onDragStart={(e) => e.preventDefault()}
        >
          <OpenInFullRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Mensagens do pedido">
        <IconButton
          size="small"
          aria-label="mensagens do pedido"
          onClick={(e) => {
            e.stopPropagation();
            onOpenChat();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onDragStart={(e) => e.preventDefault()}
        >
          <ChatBubbleOutline fontSize="small" />
        </IconButton>
      </Tooltip>

      {!!onOpenMenu && (
        <IconButton
          size="small"
          aria-label="mais ações"
          onClick={(e) => {
            e.stopPropagation();
            onOpenMenu(e);
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  </Box>
);

const DraggableCard = ({
  card,
  stageId,
  onDragStart,
  onDragOver,
  isDragged,
  compact = false,
  onChangeStatus, // opcional: callback (id, newStatus)
}) => {
  const { updateMutation } = useRequests(); // hook para atualizar no servidor

  const [menuEl, setMenuEl] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [localStatus, setLocalStatus] = useState(normalizeStatusInput(card?.status));
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    setLocalStatus(normalizeStatusInput(card?.status));
  }, [card?.status]);

  const openMenu = (e) => setMenuEl(e.currentTarget);
  const closeMenu = () => setMenuEl(null);

  /** Atualiza status: UI otimista + persistência no backend + rollback em erro */
  const persistStatus = (newStatus) => {
    const normalized = normalizeStatusInput(newStatus);
    if (normalized === localStatus) {
      closeMenu();
      return;
    }

    const prev = localStatus;
    setLocalStatus(normalized); // otimista
    setStatusLoading(true);

    updateMutation.mutate(
      { id: card?.id, status: normalized },
      {
        onSuccess: () => {
          setStatusLoading(false);
          closeMenu();
          onChangeStatus?.(card?.id, normalized);
        },
        onError: (err) => {
          setLocalStatus(prev);
          setStatusLoading(false);
          closeMenu();
          // eslint-disable-next-line no-console
          console.error("Erro ao atualizar status no servidor:", err);
        },
      }
    );
  };

  const channelIcon = getChannelIcon(card?.channelType);
  const items = useMemo(() => (Array.isArray(card?.items) ? card.items : []), [card?.items]);

  const addressLine = useMemo(() => {
    const a = card?.address;
    if (!a) return "";
    const s = a.street || "";
    const tail = a.city && a.uf ? `${a.city}, ${a.uf}` : a.city || a.uf || "";
    return [s, tail].filter(Boolean).join(" — ");
  }, [card]);

  const userName = card?.user?.name || null;
  const userLabel = userName || "Sem usuário";

  /* ====== COMPACTO ====== */
  if (compact) {
    return (
      <>
        <Card
          draggable
          onDragStart={(e) => onDragStart(e, card, stageId)}
          onDragOver={(e) => onDragOver(e, stageId, null)}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: 0,
            "&:hover": { boxShadow: 2 },
            opacity: isDragged ? 0.7 : 1,
            cursor: "grab",
          }}
        >
          <CardContent sx={{ p: 1.25, display: "grid", rowGap: 0.75 }}>
            <TopMetaBar
              orderCode={card?.orderCode}
              createdAtLabel={card?.createdAtLabel}
              status={localStatus}
              statusLoading={statusLoading}
              onOpenDetails={() => setDetailsOpen(true)}
              onOpenChat={() => {
                const width = window.screen.width;
                const height = window.screen.height;
                window.open(
                  `/popup-call/${card.id}`,
                  "SolicitaçãoPopup",
                  `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
                );
              }}
              onOpenMenu={openMenu}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.light" }}>
                {getInitials(card?.companyName || "C")}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={textWrap} title={card?.companyName}>
                  {card?.companyName || "Cliente"}
                </Typography>

                {/* Usuário responsável */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.4, mt: 0.25 }}
                  title={userLabel}
                >
                  Usuário: <strong>{userLabel}</strong>
                </Typography>

                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ columnGap: 0.75, rowGap: 0.25, mt: 0.5 }}
                >
                  <Chip size="small" icon={channelIcon} label={card?.channelType || "Web"} variant="outlined" />

                  <Chip
                    label={money(card?.total)}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  />

                  {!!card?.orderCode && (
                    <Typography variant="caption" color="text.secondary" title={`#${card.orderCode}`}>
                      #{card.orderCode}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Menu de ações */}
        <Menu
          anchorEl={menuEl}
          open={Boolean(menuEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            disabled={statusLoading}
            onClick={() => persistStatus(5)} // 5 = Cancelada
          >
            <CancelIcon fontSize="small" style={{ marginRight: 8 }} />
            Remover
          </MenuItem>
        </Menu>

        <OrderDetailsDialog open={detailsOpen} onClose={() => setDetailsOpen(false)} card={card} />
      </>
    );
  }

  /* ====== DETALHADO ====== */
  return (
    <>
      <Card
        draggable
        onDragStart={(e) => onDragStart(e, card, stageId)}
        onDragOver={(e) => onDragOver(e, stageId, null)}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: 0,
          "&:hover": { boxShadow: 2 },
          opacity: isDragged ? 0.7 : 1,
          cursor: "grab",
        }}
      >
        <CardContent sx={{ p: 1.25, display: "grid", rowGap: 1 }}>
          <TopMetaBar
            orderCode={card?.orderCode}
            createdAtLabel={card?.createdAtLabel}
            status={localStatus}
            statusLoading={statusLoading}
            onOpenDetails={() => setDetailsOpen(true)}
            onOpenChat={() => {
              const width = window.screen.width;
              const height = window.screen.height;
              window.open(
                `/popup-call/${card.id}`,
                "SolicitaçãoPopup",
                `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
              );
            }}
            onOpenMenu={openMenu}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.light" }}>
              {getInitials(card?.companyName || "C")}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={textWrap}
                title={card?.companyName}
              >
                {card?.companyName || "Cliente"}
              </Typography>

              {/* Usuário responsável */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.25 }}
                title={userLabel}
              >
                Usuário: <strong>{userLabel}</strong>
              </Typography>

              <Stack
                direction="row"
                flexWrap="wrap"
                sx={{ columnGap: 1, rowGap: 0.25, mt: 0.5 }}
                alignItems="center"
              >
                <Chip size="small" icon={channelIcon} label={card?.channelType || "Web"} variant="outlined" />
              </Stack>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.75 }}>
            <InfoPill label="Subtotal" value={money(card?.subtotal)} />
            <InfoPill label="Frete" value={money(card?.shippingCost)} />
            <InfoPill label="Total" value={money(card?.total)} highlight />
          </Box>

          <Divider />

          {items.slice(0, 2).map((it, idx) => (
            <InfoRow
              key={idx}
              icon={<ShoppingCartRoundedIcon sx={{ fontSize: 18 }} />}
              after={
                idx === 0 && items.length > 1 ? (
                  <Chip size="small" variant="outlined" label={`${items.length} itens`} />
                ) : null
              }
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2" sx={textWrap}>
                  <strong>{it.qty}×</strong> {String(it.name || "Produto")}
                </Typography>

                {it.sku && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.2 }}
                  >
                    SKU: {it.sku}
                  </Typography>
                )}
              </Box>
            </InfoRow>
          ))}

          <Divider />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              rowGap: 0.75,
              "@media (min-width:600px)": { gridTemplateColumns: "1fr 1fr", columnGap: 0.75 },
            }}
          >
            {!!card?.payment && (
              <InfoRow icon={<PaymentsRoundedIcon sx={{ fontSize: 18, opacity: 0.75 }} />}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                  <Typography variant="body2" sx={textWrap}>
                    {card.payment.methodLabel || "Pagamento"}
                  </Typography>
                  {/* {card.payment.statusLabel && (
                    <Chip
                      size="small"
                      label={card.payment.statusLabel}
                      color={toneByPayment(card.payment.statusLabel)}
                      sx={{ height: 20 }}
                    />
                  )} */}
                </Stack>
              </InfoRow>
            )}

            {!!card?.shipping && (
              <InfoRow icon={<LocalShippingRoundedIcon sx={{ fontSize: 18, opacity: 0.75 }} />}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                  <Typography variant="body2" sx={textWrap}>
                    {card.shipping.methodLabel || "Envio"}
                  </Typography>
                  {card.shipping.statusLabel && (
                    <Chip size="small" label={card.shipping.statusLabel} sx={{ height: 20 }} />
                  )}
                </Stack>
              </InfoRow>
            )}
          </Box>

          <Divider />

          {!!addressLine && (
            <InfoRow icon={<RoomRoundedIcon sx={{ fontSize: 18 }} />}>
              <Typography variant="body2" sx={textWrap}>
                {addressLine}
              </Typography>
            </InfoRow>
          )}
        </CardContent>
      </Card>

      {/* Menu de ações */}
      <Menu
        anchorEl={menuEl}
        open={Boolean(menuEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          disabled={statusLoading}
          onClick={() => persistStatus(5)} // 5 = Cancelada
        >
          <CancelIcon fontSize="small" style={{ marginRight: 8 }} />
          Remover Card
        </MenuItem>
      </Menu>

      <OrderDetailsDialog open={detailsOpen} onClose={() => setDetailsOpen(false)} card={card} />
    </>
  );
};

export default DraggableCard;
